import { Location, NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { BaseFormComponent } from 'components/form/base-form';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { VISIT_ROOM_TOPIC_ADD } from 'components/service/visit-room/visit-room-topic-form/visit-room-topic-form.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ApiService } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { ToastService } from 'services/toast.service';
import { Breadcrumb } from 'types/breadcrumb.interface';
import {
  CreateResearchTopicRequest,
  PURPOSE_EDUCATION,
  PURPOSE_WORK_TASK,
  ReaderCardByClientId,
  ResearchTopicByIdCard,
  UpdateResearchTopicRequest
} from 'types/client.interface';
import { FILE_OBJECT_TYPE_RESEARCH_TOPIC, FILE_OBJECT_TYPE_RESEARCH_TOPIC_JUSTIFICATION } from 'types/file.interface';
import {
  MAX_LENGTH_ADDRESS,
  MAX_LENGTH_COMPANY_REGISTRATION_NUMBER,
  MAX_LENGTH_EDUCATION_REGISTRATION_NUMBER,
  MAX_LENGTH_EMAIL,
  MAX_LENGTH_FACULTY,
  MAX_LENGTH_NAME,
  MAX_LENGTH_PHONE,
  MAX_LENGTH_TOPIC,
  MIN_LENGTH_EMAIL,
  MIN_LENGTH_PHONE,
  MIN_LENGTH_TOPIC,
  emailValidator,
  getControlsWithErrors,
  phoneValidator
} from 'util/form.utility';
import { SpinnerComponent } from '../../components/spinner/spinner.component';

export const FILE_UPLOAD_COMPONENT_COUNT = 2;
export const VISIT_ROOM_TOPIC_ID = 'visitRoomTopicId';
export const NOTIFICATION_TIME_LONG = 10000;
export const NOTIFICATION_TIME_SHORT = 3000;

@Component({
  selector: 'app-research-topic-form-page',
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    TranslateModule,
    ReactiveFormsModule,
    NgClass,
    TooltipComponent,
    ConfirmModalComponent,
    SpinnerComponent,
    FileUploadComponent
  ],
  templateUrl: './research-topic-form-page.component.html',
  styleUrls: ['../../components/form/base-form.scss', './research-topic-form-page.component.scss']
})
export class ResearchTopicFormPageComponent extends BaseFormComponent implements OnInit {
  public readonly topicFileType = FILE_OBJECT_TYPE_RESEARCH_TOPIC;
  public readonly justificationFileType = FILE_OBJECT_TYPE_RESEARCH_TOPIC_JUSTIFICATION;
  public readonly maxFieldLength = MAX_LENGTH_NAME;
  public readonly maxLengthPhone = MAX_LENGTH_PHONE;
  public readonly maxLengthEmail = MAX_LENGTH_EMAIL;
  public readonly maxLengthFaculty = MAX_LENGTH_FACULTY;
  public readonly maxLengthRegistrationNumber = MAX_LENGTH_COMPANY_REGISTRATION_NUMBER;
  public readonly maxLengthRegistrationNumberEducation = MAX_LENGTH_EDUCATION_REGISTRATION_NUMBER;

  workForm: FormGroup;
  educationForm: FormGroup;
  breadcrumbs: Breadcrumb[] = [];
  isReadersCardLoading: boolean = true;
  isResearchTopicsLoading: boolean = true;
  isSubmitLoading: boolean = false;
  isSubmittingFiles: boolean = false;
  isCreate: boolean = true;

  topicId?: string;
  researchTopic?: ResearchTopicByIdCard;
  readersCard?: ReaderCardByClientId;
  clientId?: string;

  get name(): AbstractControl<any, any> | null {
    return this.form.get('name');
  }
  get purpose(): AbstractControl<any, any> | null {
    return this.form.get('purpose');
  }
  get chronologicalBoundaries(): AbstractControl<any, any> | null {
    return this.form.get('chronologicalBoundaries');
  }
  get researchGoalAndAccessReason(): AbstractControl<any, any> | null {
    return this.form.get('researchGoalAndAccessReason');
  }
  get notes(): AbstractControl<any, any> | null {
    return this.form.get('notes');
  }
  get requiresRestrictedAccess(): AbstractControl<any, any> | null {
    return this.form.get('requiresRestrictedAccess');
  }
  get restrictedAccessJustification(): AbstractControl<any, any> | null {
    return this.form.get('restrictedAccessJustification');
  }

  get workRegistrationNumber(): AbstractControl<any, any> | null {
    return this.workForm.get('registrationNumber');
  }
  get workName(): AbstractControl<any, any> | null {
    return this.workForm.get('name');
  }
  get workAddress(): AbstractControl<any, any> | null {
    return this.workForm.get('address');
  }
  get workPhone(): AbstractControl<any, any> | null {
    return this.workForm.get('phone');
  }
  get workEmail(): AbstractControl<any, any> | null {
    return this.workForm.get('email');
  }

  get educationRegistrationNumber(): AbstractControl<any, any> | null {
    return this.educationForm.get('registrationNumber');
  }
  get educationName(): AbstractControl<any, any> | null {
    return this.educationForm.get('name');
  }
  get educationFaculty(): AbstractControl<any, any> | null {
    return this.educationForm.get('faculty');
  }
  get educationAddress(): AbstractControl<any, any> | null {
    return this.educationForm.get('address');
  }

  get isInitialLoading(): boolean {
    return this.isReadersCardLoading || this.isResearchTopicsLoading;
  }

  get isLoading(): boolean {
    return this.isReadersCardLoading || this.isResearchTopicsLoading || this.isSubmitLoading;
  }

  constructor(
    protected override location: Location,
    protected override router: Router,
    protected override formBuilder: FormBuilder,
    protected override apiService: ApiService,
    protected authService: AuthorizationService,
    protected override toastService: ToastService,
    protected activatedRoute: ActivatedRoute,
    protected override translateService: TranslateService
  ) {
    super(location, router, formBuilder, apiService, toastService, translateService);
    this.workForm = this.formBuilder.group({});
    this.fileUploadComponentCount = FILE_UPLOAD_COMPONENT_COUNT;
    this.educationForm = this.formBuilder.group({});
  }

  override ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      if (window.location.href.includes('rediget')) {
        this.topicId = params['id'];
        this.clientId = params['clientId'];
      } else {
        this.clientId = params['clientId'];
      }

      const clientId = this.clientId || this.readersCard?.clientId;

      this.apiService.getReadersCard(clientId!).subscribe(
        (data) => {
          this.readersCard = data;
          this.isReadersCardLoading = false;
        },
        (error: any) => {
          console.error(error);
        }
      );

      if (this.topicId) {
        this.isCreate = false;
        this.apiService.getResearchTopicById(this.topicId).subscribe((data) => {
          this.researchTopic = data;
          this.isResearchTopicsLoading = false;
          this.fillForm();
          this.updateRestrictedJustificationValidators();
        });
      } else {
        this.isResearchTopicsLoading = false;
        this.fillForm();
        this.updateRestrictedJustificationValidators();
      }
    });
  }

  private updateRestrictedJustificationValidators(): void {
    const value = this.requiresRestrictedAccess?.value;

    if (value) {
      this.restrictedAccessJustification?.setValidators([
        Validators.required,
        Validators.minLength(MIN_LENGTH_TOPIC),
        Validators.maxLength(MAX_LENGTH_TOPIC)
      ]);
    } else {
      this.restrictedAccessJustification?.clearValidators();
    }

    this.restrictedAccessJustification?.updateValueAndValidity();
  }

  override fillForm(): void {
    this.form = this.formBuilder.group({
      name: [this.researchTopic?.name || '', [Validators.required, Validators.maxLength(MAX_LENGTH_NAME)]],
      purpose: [this.researchTopic?.purpose || '', [Validators.required, Validators.maxLength(MAX_LENGTH_NAME)]],
      chronologicalBoundaries: [
        this.researchTopic?.chronologicalBoundaries || '',
        [Validators.required, Validators.maxLength(MAX_LENGTH_TOPIC), Validators.minLength(MIN_LENGTH_TOPIC)]
      ],
      researchGoalAndAccessReason: [
        this.researchTopic?.researchGoalAndAccessReason || '',
        [Validators.required, Validators.maxLength(MAX_LENGTH_TOPIC), Validators.minLength(MIN_LENGTH_TOPIC)]
      ],
      notes: [this.researchTopic?.notes || '', [Validators.maxLength(MAX_LENGTH_NAME), Validators.minLength(MIN_LENGTH_TOPIC)]],
      requiresRestrictedAccess: [
        this.researchTopic?.requiresRestrictedAccess || false,
        [Validators.required, Validators.maxLength(MAX_LENGTH_TOPIC)]
      ],
      restrictedAccessJustification: [
        this.researchTopic?.restrictedAccessJustification || '',
        [Validators.minLength(MIN_LENGTH_TOPIC), Validators.maxLength(MAX_LENGTH_TOPIC)]
      ]
    });
    this.form.get('requiresRestrictedAccess')?.valueChanges.subscribe(() => {
      this.updateRestrictedJustificationValidators();
    });

    this.workForm = this.formBuilder.group({
      registrationNumber: [
        this.researchTopic?.work?.registrationNumber || '',
        [Validators.required, Validators.maxLength(MAX_LENGTH_COMPANY_REGISTRATION_NUMBER)]
      ],
      name: [this.researchTopic?.work?.name || '', [Validators.required, Validators.maxLength(MAX_LENGTH_NAME)]],
      address: [this.researchTopic?.work?.address || '', [Validators.required, Validators.maxLength(MAX_LENGTH_ADDRESS)]],
      phone: [
        this.researchTopic?.work?.phone || '',
        [Validators.required, phoneValidator(), Validators.minLength(MIN_LENGTH_PHONE), Validators.maxLength(MAX_LENGTH_PHONE)]
      ],
      email: [
        this.researchTopic?.work?.email || '',
        [Validators.required, emailValidator(), Validators.minLength(MIN_LENGTH_EMAIL), Validators.maxLength(MAX_LENGTH_EMAIL)]
      ]
    });

    this.educationForm = this.formBuilder.group({
      registrationNumber: [
        this.researchTopic?.study?.registrationNumber || '',
        [Validators.required, Validators.maxLength(MAX_LENGTH_EDUCATION_REGISTRATION_NUMBER)]
      ],
      name: [this.researchTopic?.study?.name || '', [Validators.required, Validators.maxLength(MAX_LENGTH_NAME)]],
      faculty: [this.researchTopic?.study?.faculty || '', [Validators.required, Validators.maxLength(MAX_LENGTH_FACULTY)]],
      address: [this.researchTopic?.study?.address || '', [Validators.required, Validators.maxLength(MAX_LENGTH_ADDRESS)]]
    });
  }

  hasWorkSection(): boolean {
    return this.purpose?.value === PURPOSE_WORK_TASK;
  }

  hasEducationSection(): boolean {
    return this.purpose?.value === PURPOSE_EDUCATION;
  }

  override submit(): void {
    this.submitAttempted = true;

    if (this.isFormValid()) {
      this.isSubmitLoading = true;

      if (this.topicId) {
        const args: UpdateResearchTopicRequest = {
          id: this.topicId,
          ...this.form.value,
          ...this.getConditionalFormValues(),
          clientId: this.readersCard?.clientId,
          readerCardId: this.readersCard?.id,
          isActive: true,
          restrictedAccessJustification: this.requiresRestrictedAccess?.value ? this.restrictedAccessJustification?.value || '' : ''
        };

        this.fileUploadComponentCount = this.requiresRestrictedAccess?.value
          ? FILE_UPLOAD_COMPONENT_COUNT
          : FILE_UPLOAD_COMPONENT_COUNT - 1;
        this.apiService.updateResearchTopic(args).subscribe(
          () => {
            this.isSubmittingFiles = true;
          },
          (error) => {
            try {
              if (error.error.errors['ResearchTopic'][0] === 'Such name already exists.') {
                this.toastService.showDanger('topicTitleExists', '', {
                  shouldTranslate: true
                });
              } else {
                this.toastService.showRequestError();
              }
            } catch {
              this.toastService.showRequestError();
            }
            this.isSubmitLoading = false;
          }
        );
      } else {
        const args: CreateResearchTopicRequest = {
          ...this.form.value,
          ...this.getConditionalFormValues(),
          clientId: this.clientId,
          readerCardId: this.readersCard?.id,
          isActive: true,
          restrictedAccessJustification: this.requiresRestrictedAccess?.value ? this.restrictedAccessJustification?.value : ''
        };

        this.fileUploadComponentCount = this.requiresRestrictedAccess?.value
          ? FILE_UPLOAD_COMPONENT_COUNT
          : FILE_UPLOAD_COMPONENT_COUNT - 1;
        this.apiService.createResearchTopic(args).subscribe(
          (data: ResearchTopicByIdCard) => {
            let storedTopics: string[] = [];
            const storedValue = localStorage.getItem(VISIT_ROOM_TOPIC_ID);
            if (storedValue) {
              try {
                storedTopics = JSON.parse(storedValue);
                if (!Array.isArray(storedTopics)) {
                  storedTopics = [];
                }
              } catch (error) {
                console.error('Error parsing stored topic IDs:', error);
                storedTopics = [];
              }
            }

            storedTopics.push(data.id!);
            localStorage.setItem(VISIT_ROOM_TOPIC_ID, JSON.stringify(storedTopics));

            this.topicId = data.id;
            this.isSubmittingFiles = true;
          },
          (error) => {
            try {
              if (error.error.errors['ResearchTopic'][0] === 'Such name already exists.') {
                this.toastService.showDanger('topicTitleExists', '', {
                  shouldTranslate: true
                });
              } else {
                this.toastService.showRequestError();
              }
            } catch {
              this.toastService.showRequestError();
            }
            this.isSubmitLoading = false;
          }
        );
      }
    } else {
      const errors = getControlsWithErrors(this.form);

      if (this.hasWorkSection()) {
        const workErrors = getControlsWithErrors(this.workForm);

        errors.push(...workErrors.map((error) => 'work' + error.charAt(0).toUpperCase() + error.slice(1)));
      }

      if (this.hasEducationSection()) {
        const educationErrors = getControlsWithErrors(this.educationForm);

        errors.push(...educationErrors.map((error) => 'edu' + error.charAt(0).toUpperCase() + error.slice(1)));
      }

      this.toastService.showFormValidationError(errors, 'pages.researchTopic');
    }
  }

  protected override onSubmitSuccess(): void {
    this.isSubmitLoading = false;
    this.isSubmittingFiles = false;
    this.uploadComponentsReceived = 0;

    const visitRoomId = localStorage.getItem(VISIT_ROOM_TOPIC_ADD);
    localStorage.removeItem(VISIT_ROOM_TOPIC_ADD);

    if (this.isCreate) {
      this.toastService.showSuccess(visitRoomId ? 'createVisitTopicSuccessTitle' : 'createTopicSuccessTitle', '', {
        shouldTranslate: true
      });
    } else {
      this.toastService.showSuccess(visitRoomId ? 'updateVisitTopicSuccessTitle' : 'updateTopicSuccessTitle', '', {
        shouldTranslate: true
      });
    }

    if (visitRoomId) {
      this.router.navigate([`/pakalpojumi/lasitavas-apmeklejums/${visitRoomId}`], { queryParams: { solis: '2' } });
    } else {
      this.router.navigate([`/klients/${this.clientId}`]);
    }
  }

  isFormValid(): boolean {
    switch (this.purpose?.value) {
      case PURPOSE_WORK_TASK:
        return this.form.valid && this.workForm.valid;
      case PURPOSE_EDUCATION:
        return this.form.valid && this.educationForm.valid;
      default:
        return this.form.valid;
    }
  }

  getConditionalFormValues(): any {
    switch (this.purpose?.value) {
      case PURPOSE_WORK_TASK:
        return {
          work: this.workForm.value
        };
      case PURPOSE_EDUCATION:
        return {
          study: this.educationForm.value
        };
      default:
        return {};
    }
  }

  setRestrictedAccessJustificationValidators(): void {
    if (this.researchTopic?.requiresRestrictedAccess) {
      this.restrictedAccessJustification?.setValidators([
        Validators.required,
        Validators.minLength(MIN_LENGTH_TOPIC),
        Validators.maxLength(MAX_LENGTH_TOPIC)
      ]);
    }
  }
}
